import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { useLanguage } from '../contexts/LanguageContext';
import { loadBlogPosts } from '../utils/blogLoader';
import { formatDate } from '../utils/date';
import { BlogPost } from '../types';
import VGWortPixel from './VGWortPixel';
import './BlogSection.css';


const BlogSection: React.FC = () => {
  const { t, language } = useLanguage();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        const blogPosts = await loadBlogPosts(language);
        setPosts(blogPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [language]);

  const allTags = Array.from(new Set(posts.flatMap(post => post.tags)));
  
  const filteredPosts = selectedTag
    ? posts.filter(post => {
        const tags = (post.tags || []).map(tag => tag.toLowerCase());
        return tags.includes(selectedTag.toLowerCase());
      })
    : posts;


  const renderContent = (content: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                customStyle={{
                  margin: '16px 0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };



  if (selectedPost) {
    return (
      <div className="blog-section">
        <div className="blog-header">
          <button 
            className="back-button"
            onClick={() => setSelectedPost(null)}
          >
            ← {t('blog.back')}
          </button>
        </div>
        
        <article className="blog-post-full">
          <header className="post-header">
            <h1>{selectedPost.title}</h1>
            <div className="post-meta">
              <span className="post-date">{formatDate(selectedPost.date)}</span>
              <span className="post-read-time">{selectedPost.readTime}</span>
            </div>
            <div className="post-tags">
              {selectedPost.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          </header>
          
          <div className="post-content">
            {renderContent(selectedPost.content)}
          </div>
          
          {/* VG Wort tracking pixel for monetization */}
          <VGWortPixel 
            pixelUrl={selectedPost.vgWortPixel} 
            title={selectedPost.title} 
          />
        </article>
      </div>
    );
  }

  return (
    <div className="blog-section">
      <div className="blog-header">
        <div>
          <h2>{t('blog.title')}</h2>
          <p>{t('blog.description')}</p>
        </div>
      </div>

      <div className="blog-filters">
        <button 
          className={selectedTag === '' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setSelectedTag('')}
        >
          {t('blog.all')}
        </button>
        {allTags.map(tag => (
          <button
            key={tag}
            className={selectedTag === tag ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setSelectedTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="blog-loading">
          <p>{t('blog.loading')}</p>
        </div>
      ) : (
        <div className="blog-posts">
          {filteredPosts.length === 0 ? (
            <div className="no-posts">
              <p>{t('blog.noPosts')}</p>
            </div>
          ) : (
            filteredPosts.map(post => (
              <article key={post.id} className="blog-post-card">
            <div className="post-card-header">
              <h3 onClick={() => setSelectedPost(post)}>{post.title}</h3>
              <div className="post-meta">
                <span className="post-date">{formatDate(post.date)}</span>
                <span className="post-read-time">{post.readTime}</span>
              </div>
            </div>
            
            <p className="post-excerpt">{post.excerpt}</p>
            
            <div className="post-card-footer">
              <div className="post-tags">
                {post.tags && post.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
              <button 
                className="read-more-btn"
                onClick={() => setSelectedPost(post)}
              >
                {t('blog.readMore')} →
              </button>
            </div>
              </article>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BlogSection;